"use client";

import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import { TryFreeButton } from "@/components/root/try-free-button";
import { Twitter, Linkedin, Github, Instagram } from "lucide-react";

export function Footer() {
  const rootT = useTranslations();
  const t = useTranslations("Footer");
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-accent text-white">
      <div className="container max-w-[1540px] py-10 px-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2 flex flex-col h-full">
            <div>
              <h2 className="text-3xl font-normal text-brand-primary mb-8 mr-20">
                {t("title.start")}
                <span className="text-brand text-white">{t("title.end")}</span>
              </h2>
              <TryFreeButton variant="white" />
            </div>

            <div className="flex space-x-4 mt-auto pt-8">
              {[Twitter, Linkedin, Github, Instagram].map((Icon, index) => (
                <a
                  key={index}
                  href="#"
                  className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center"
                >
                  <Icon className="h-4 w-4 text-black" />
                </a>
              ))}
            </div>
          </div>

          <FooterLinkGroup
            title={t("product.title")}
            links={[
              { href: "/app", label: t("product.app") },
              { href: "/assistants", label: t("product.assistants") },
              { href: "/features", label: t("product.features") },
            ]}
          />

          <div className="space-y-6">
            <FooterLinkGroup
              title={t("company.title")}
              links={[
                { href: "/about", label: t("company.about") },
                { href: "/pricing", label: t("company.pricing") },
                { href: "/team", label: t("company.team") },
                { href: "/contacts", label: t("company.contacts") },
              ]}
            />
            <FooterLinkGroup
              title={t("comparison.title")}
              links={[{ href: "/vs/gpt", label: t("comparison.gpt") }]}
            />
          </div>

          <div className="space-y-6">
            <FooterLinkGroup
              title={t("support.title")}
              links={[
                { href: "/knowledge", label: t("support.knowledge") },
                { href: "/blog", label: t("support.blog") },
                { href: "/contacts", label: t("support.email") },
              ]}
            />
            <FooterLinkGroup
              title={t("legal.title")}
              links={[
                { href: "/privacy-policy", label: t("legal.privacy") },
                { href: "/terms-of-use", label: t("legal.terms") },
              ]}
            />
          </div>

          <FooterLinkGroup
            title={rootT("Landing.Assistants.title")}
            links={[
              {
                href: "/assistants/tax",
                label: rootT("Landing.Assistants.assistant1.simpleName"),
              },
              {
                href: "/assistants/reports",
                label: rootT("Landing.Assistants.assistant2.simpleName"),
              },
              {
                href: "/assistants/laws",
                label: rootT("Landing.Assistants.assistant3.simpleName"),
              },
              {
                href: "/assistants/expenses",
                label: rootT("Landing.Assistants.assistant4.simpleName"),
              },
              {
                href: "/assistants/payroll",
                label: rootT("Landing.Assistants.assistant5.simpleName"),
              },
              {
                href: "/assistants/analysis",
                label: rootT("Landing.Assistants.assistant6.simpleName"),
              },
              {
                href: "/assistants/hr",
                label: rootT("Landing.Assistants.assistant7.simpleName"),
              },
              {
                href: "/assistants/forecast",
                label: rootT("Landing.Assistants.assistant8.simpleName"),
              },
              {
                href: "/assistants/contributions",
                label: rootT("Landing.Assistants.assistant9.simpleName"),
              },
              {
                href: "/assistants/pricing",
                label: rootT("Landing.Assistants.assistant10.simpleName"),
              },
              {
                href: "/assistants/vat",
                label: rootT("Landing.Assistants.assistant11.simpleName"),
              },
              {
                href: "/assistants/basics",
                label: rootT("Landing.Assistants.assistant12.simpleName"),
              },
              {
                href: "/assistants/contracts",
                label: rootT("Landing.Assistants.assistant13.simpleName"),
              },
              {
                href: "/assistants/currency",
                label: rootT("Landing.Assistants.assistant14.simpleName"),
              },
              {
                href: "/assistants/audit",
                label: rootT("Landing.Assistants.assistant15.simpleName"),
              },
              {
                href: "/assistants/help",
                label: rootT("Landing.Assistants.assistant16.simpleName"),
              },
              {
                href: "/assistants/advice",
                label: rootT("Landing.Assistants.assistant17.simpleName"),
              },
              {
                href: "/assistants/organize",
                label: rootT("Landing.Assistants.assistant18.simpleName"),
              },
            ]}
          />
        </div>
      </div>

      <div className="border-t border-app-border">
        <div className="container max-w-[1540px] py-6">
          <p className="text-sm text-gray-400 text-center">
            © {currentYear} {t("copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
}

interface FooterLinkGroupProps {
  title: string;
  links: Array<{
    href: string;
    label: string;
  }>;
}

export function FooterLinkGroup({ title, links }: FooterLinkGroupProps) {
  const locale = useLocale();
  return (
    <div>
      <h3 className="text-[18px] font-normal mb-2">{title}</h3>
      <ul className="text-[14px] space-y-1.5 text-gray-400">
        {links.map((link, index) => (
          <li key={index}>
            <Link href={`/${locale}${link.href}`}>{link.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
